/**
 * PermissionMatrixTab Component
 * 
 * Admin panel tab for visualizing role permissions as a matrix
 * 
 * Features:
 * - Interactive grid: rows = role templates, columns = permissions
 * - Filter by department, authority level
 * - Color-coded by authority level
 * - Export to CSV
 * - Click on role to see users
 * 
 * Created: 2026-01-26 (Permission System V2 Phase 3)
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { useToast } from '../../hooks/useToast';
import useTableSort from '../../hooks/useTableSort';
import { FilterTemplateDropdown, FilterIcon, useFilterTemplate } from '../common/FilteringTemplate';

// Authority level colors
const AUTHORITY_COLORS = {
  'System': '#dc3545',
  'Upper Management': '#007bff',
  'Manager': '#28a745',
  'Lead': '#fd7e14',
  'Staff': '#6c757d'
};

// All known permissions (collected from role definitions)
const ALL_PERMISSIONS = [
  '*',
  'view_all', 'edit_all', 'delete', 'approve', 'export',
  'admin', 'manage_users', 'system_config', 'manage_project_users',
  'view_assigned', 'edit_own', 'upload',
  'approve_own_trades', 'approve_crew',
  'view_own_crew', 'edit_technical_docs', 'edit_schedule',
  'edit_payroll', 'edit_payables', 'approve_invoices',
  'approve_estimates', 'create_requisitions', 'approve_pos',
  'stop_work', 'create_incidents', 'reject_work', 'create_inspections',
  'view_admin', 'edit_admin', 'create_admin', 'schedule',
  'view_all_docs', 'upload_docs', 'organize_docs', 'edit_metadata',
  'edit_sales', 'create_opportunities',
  'edit_inventory', 'assign_tools', 'create_orders',
  'view_inventory', 'receive_tools'
];

const PermissionMatrixTab = () => {
  const { instance, accounts } = useMsal();
  const { showToast } = useToast();
  
  // Data state
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter state
  const [filterDepartment, setFilterDepartment] = useState('');
  const [filterAuthority, setFilterAuthority] = useState('');
  const [matrixSearch, setMatrixSearch] = useState('');
  
  // Expanded role (to show users)
  const [expandedRole, setExpandedRole] = useState(null);
  const [roleUsers, setRoleUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Fetch roles and departments
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [rolesRes, deptsRes] = await Promise.all([
        authenticatedApiCall(instance, accounts, 'GET', '/role-templates'),
        authenticatedApiCall(instance, accounts, 'GET', '/role-templates/departments')
      ]);
      
      if (rolesRes.data.success) {
        setRoles(rolesRes.data.data || []);
      }
      if (deptsRes.data.success) {
        setDepartments(deptsRes.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [instance, accounts]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch users for a role
  const fetchRoleUsers = useCallback(async (roleId) => {
    try {
      setLoadingUsers(true);
      
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        `/role-templates/${roleId}/users`
      );
      
      if (response.data.success) {
        setRoleUsers(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching role users:', err);
      setRoleUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  }, [instance, accounts]);

  // Toggle role expansion
  const toggleRoleExpand = (roleId) => {
    if (expandedRole === roleId) {
      setExpandedRole(null);
      setRoleUsers([]);
    } else {
      setExpandedRole(roleId);
      fetchRoleUsers(roleId);
    }
  };

  // Filter roles
  const filteredRoles = useMemo(() => {
    return roles.filter(role => {
      if (filterDepartment && role.department_id !== parseInt(filterDepartment)) {
        return false;
      }
      if (filterAuthority && role.authority_level !== filterAuthority) {
        return false;
      }
      if (matrixSearch.trim()) {
        const q = matrixSearch.toLowerCase();
        const dept = departments.find(d => d.id === role.department_id);
        const deptName = dept ? dept.name.toLowerCase() : '';
        if (!role.display_name.toLowerCase().includes(q) && !deptName.includes(q) && !(role.authority_level || '').toLowerCase().includes(q)) {
          return false;
        }
      }
      return true;
    });
  }, [roles, filterDepartment, filterAuthority, matrixSearch, departments]);

  const activePermissions = useMemo(() => {
    const perms = new Set();
    filteredRoles.forEach(role => {
      const rolePerms = role.permissions || [];
      rolePerms.forEach(p => perms.add(p));
    });
    const sorted = Array.from(perms).sort((a, b) => {
      if (a === '*') return -1;
      if (b === '*') return 1;
      return a.localeCompare(b);
    });
    return sorted;
  }, [filteredRoles]);

  const matrixColumns = useMemo(() => [
    { key: 'display_name', label: 'Role', type: 'text' },
    { key: 'department_display', label: 'Department', type: 'text' },
    { key: 'authority_level', label: 'Authority', type: 'text' },
    ...activePermissions.map(p => ({ key: `perm__${p}`, label: p, type: 'text' })),
  ], [activePermissions]);

  const matrixSort = useTableSort({ columns: matrixColumns });

  const flatMatrixRoles = useMemo(() => filteredRoles.map(role => {
    const dept = departments.find(d => d.id === role.department_id);
    const flat = {
      ...role,
      department_display: dept?.name || '',
    };
    activePermissions.forEach(p => {
      const perms = role.permissions || [];
      flat[`perm__${p}`] = (perms.includes(p) || perms.includes('*')) ? '✓' : '';
    });
    return flat;
  }), [filteredRoles, departments, activePermissions]);

  const matrixFilter = useFilterTemplate({ items: flatMatrixRoles, columns: matrixColumns });

  const sortedFilteredRoles = useMemo(() => {
    return matrixSort.applySorting(matrixFilter.filteredItems);
  }, [matrixFilter.filteredItems, matrixSort]);

  // Check if role has permission
  const hasPermission = (role, permission) => {
    const perms = role.permissions || [];
    return perms.includes(permission) || perms.includes('*');
  };

  // Export to CSV
  const handleExport = () => {
    const headers = ['Role', 'Department', 'Authority Level', ...activePermissions];
    
    const rows = filteredRoles.map(role => {
      const dept = departments.find(d => d.id === role.department_id);
      return [
        role.display_name,
        dept?.name || '',
        role.authority_level,
        ...activePermissions.map(p => hasPermission(role, p) ? 'Yes' : '')
      ];
    });
    
    const escapeCSV = (val) => {
      if (val === null || val === undefined) return '';
      const str = String(val);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };
    
    const csv = [
      headers.map(escapeCSV).join(','),
      ...rows.map(row => row.map(escapeCSV).join(','))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'permission-matrix.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    showToast('Permission matrix exported', 'success');
  };

  // Get authority levels for filter
  const authorityLevels = useMemo(() => {
    const levels = new Set(roles.map(r => r.authority_level));
    return Array.from(levels).sort((a, b) => {
      const order = ['System', 'Upper Management', 'Manager', 'Lead', 'Staff'];
      return order.indexOf(a) - order.indexOf(b);
    });
  }, [roles]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
        Loading permission matrix...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '1rem',
        backgroundColor: '#f8d7da',
        color: '#721c24',
        borderRadius: '4px'
      }}>
        {error}
      </div>
    );
  }

  return (
    <div>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '1.5rem' 
      }}>
        <div>
          <h3 style={{ margin: 0, marginBottom: '0.5rem' }}>Permission Matrix</h3>
          <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
            Visual overview of all role templates and their permissions.
          </p>
        </div>
        <button
          onClick={handleExport}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.9rem'
          }}
        >
          Export CSV
        </button>
      </div>

      {/* Filters + Legend in one row */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={filterDepartment} onChange={(e) => setFilterDepartment(e.target.value)}
          style={{ padding: '0.4rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.85rem', minWidth: '160px' }}>
          <option value="">All Departments</option>
          {departments.map(dept => (<option key={dept.id} value={dept.id}>{dept.name}</option>))}
        </select>
        <select value={filterAuthority} onChange={(e) => setFilterAuthority(e.target.value)}
          style={{ padding: '0.4rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.85rem', minWidth: '160px' }}>
          <option value="">All Authority Levels</option>
          {authorityLevels.map(level => (<option key={level} value={level}>{level}</option>))}
        </select>
        <input type="text" placeholder="Search roles..." value={matrixSearch} onChange={(e) => setMatrixSearch(e.target.value)}
          style={{ padding: '0.4rem 0.6rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.85rem', minWidth: '160px' }} />
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', fontSize: '0.8rem', marginLeft: 'auto' }}>
          {Object.entries(AUTHORITY_COLORS).map(([level, color]) => (
            <span key={level} style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
              <span style={{ width: '10px', height: '10px', backgroundColor: color, borderRadius: '2px', display: 'inline-block' }} />
              {level}
            </span>
          ))}
        </div>
      </div>

      {/* Active column filters indicator */}
      {matrixFilter.getActiveFilters().length > 0 && (
        <div style={{ padding: '0.5rem 1rem', marginBottom: '0.5rem', backgroundColor: '#d1fae5', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.8rem' }}>
          <span style={{ color: '#065f46' }}>
            {matrixFilter.getActiveFilters().length} column filter(s) active — showing {sortedFilteredRoles.length} of {filteredRoles.length} roles
          </span>
          <button onClick={() => matrixFilter.clearAllFilters()} style={{ padding: '2px 10px', fontSize: '0.75rem', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer' }}>Clear All Filters</button>
        </div>
      )}

      {/* Matrix Table */}
      <div style={{ 
        overflowY: 'scroll',
        overflowX: 'auto',
        height: '398px',
        border: '1px solid #dee2e6',
        borderRadius: '8px'
      }}>
        <table style={{ 
          borderCollapse: 'collapse', 
          fontSize: '0.85rem',
          minWidth: '100%'
        }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa' }}>
              <th style={{ 
                padding: '0.75rem', 
                textAlign: 'left', 
                borderBottom: '2px solid #dee2e6',
                position: 'sticky',
                left: 0,
                top: 0,
                backgroundColor: '#f8f9fa',
                zIndex: 3,
                minWidth: '200px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span onClick={() => matrixSort.handleSort('display_name')} style={{ cursor: 'pointer', userSelect: 'none', flex: 1 }} title="Sort by Role">
                    Role
                    {matrixSort.sortColumn === 'display_name' && (<span style={{ fontSize: '0.7rem', marginLeft: '3px' }}>{matrixSort.sortDirection === 'asc' ? '▲' : '▼'}</span>)}
                  </span>
                  <span onClick={(e) => matrixFilter.openFilterAtPosition(e, 'display_name')} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                    <FilterIcon isActive={!!(matrixFilter.columnFilters['display_name']?.length > 0 || matrixFilter.filterText['display_name'])} color="#666" />
                  </span>
                </div>
                {matrixFilter.openFilterColumn === 'display_name' && (
                  <FilterTemplateDropdown columnKey="display_name" columnLabel="Role" columnFilters={matrixFilter.columnFilters} filterMode={matrixFilter.filterMode} filterText={matrixFilter.filterText} filterSearch={matrixFilter.filterSearch} filterButtonPositions={matrixFilter.filterButtonPositions} setOpenFilterColumn={matrixFilter.setOpenFilterColumn} setFilterSearch={matrixFilter.setFilterSearch} getUniqueValues={matrixFilter.getUniqueValues} handleFilterToggle={matrixFilter.handleFilterToggle} selectAllColumnFilter={matrixFilter.selectAllColumnFilter} clearColumnFilter={matrixFilter.clearColumnFilter} applyTextFilter={matrixFilter.applyTextFilter} />
                )}
              </th>
              {activePermissions.map(perm => {
                const colKey = `perm__${perm}`;
                const isActive = !!(matrixFilter.columnFilters[colKey]?.length > 0 || matrixFilter.filterText[colKey]);
                return (
                  <th 
                    key={perm}
                    style={{ 
                      padding: '0.25rem 0.15rem', 
                      textAlign: 'center', 
                      borderBottom: '2px solid #dee2e6',
                      whiteSpace: 'nowrap',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      position: 'sticky',
                      top: 0,
                      backgroundColor: isActive ? '#d1fae5' : '#f8f9fa',
                      zIndex: 2,
                      verticalAlign: 'bottom'
                    }}
                    title={`${perm} (click header to sort)`}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '140px' }}>
                      <div
                        onClick={() => matrixSort.handleSort(colKey)}
                        style={{ writingMode: 'vertical-lr', transform: 'rotate(180deg)', flex: 1, cursor: 'pointer', userSelect: 'none', display: 'flex', alignItems: 'center' }}
                      >
                        {perm}
                        {matrixSort.sortColumn === colKey && (<span style={{ fontSize: '0.6rem', marginTop: '2px' }}>{matrixSort.sortDirection === 'asc' ? '▲' : '▼'}</span>)}
                      </div>
                      <span
                        onClick={(e) => { e.stopPropagation(); matrixFilter.openFilterAtPosition(e, colKey); }}
                        style={{ cursor: 'pointer', padding: '3px 0', flexShrink: 0 }}
                      >
                        <FilterIcon isActive={isActive} color="#666" />
                      </span>
                    </div>
                    {matrixFilter.openFilterColumn === colKey && (
                      <FilterTemplateDropdown columnKey={colKey} columnLabel={perm} columnFilters={matrixFilter.columnFilters} filterMode={matrixFilter.filterMode} filterText={matrixFilter.filterText} filterSearch={matrixFilter.filterSearch} filterButtonPositions={matrixFilter.filterButtonPositions} setOpenFilterColumn={matrixFilter.setOpenFilterColumn} setFilterSearch={matrixFilter.setFilterSearch} getUniqueValues={matrixFilter.getUniqueValues} handleFilterToggle={matrixFilter.handleFilterToggle} selectAllColumnFilter={matrixFilter.selectAllColumnFilter} clearColumnFilter={matrixFilter.clearColumnFilter} applyTextFilter={matrixFilter.applyTextFilter} />
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sortedFilteredRoles.map(role => {
              const dept = departments.find(d => d.id === role.department_id);
              const isExpanded = expandedRole === role.id;
              
              return (
                <React.Fragment key={role.id}>
                  <tr 
                    style={{ 
                      cursor: 'pointer',
                      backgroundColor: isExpanded ? '#e7f3ff' : 'white'
                    }}
                    onClick={() => toggleRoleExpand(role.id)}
                  >
                    <td style={{ 
                      padding: '0.3rem 0.75rem', 
                      borderBottom: '1px solid #dee2e6',
                      position: 'sticky',
                      left: 0,
                      backgroundColor: isExpanded ? '#e7f3ff' : 'white',
                      zIndex: 1
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ 
                          color: isExpanded ? '#007bff' : '#666',
                          fontSize: '0.75rem'
                        }}>
                          {isExpanded ? '▼' : '▶'}
                        </span>
                        <span style={{
                          width: '8px',
                          height: '8px',
                          backgroundColor: AUTHORITY_COLORS[role.authority_level] || '#6c757d',
                          borderRadius: '50%',
                          display: 'inline-block'
                        }} />
                        <div>
                          <div style={{ fontWeight: 500 }}>{role.display_name}</div>
                          <div style={{ fontSize: '0.75rem', color: '#666' }}>
                            {dept?.name || ''} • {role.authority_level}
                          </div>
                        </div>
                      </div>
                    </td>
                    {activePermissions.map(perm => {
                      const has = hasPermission(role, perm);
                      const isWildcard = (role.permissions || []).includes('*');
                      
                      return (
                        <td 
                          key={perm}
                          style={{ 
                            padding: '0.15rem 0.5rem', 
                            textAlign: 'center', 
                            borderBottom: '1px solid #dee2e6',
                            backgroundColor: has ? (isWildcard && perm !== '*' ? '#e8f5e9' : '#d4edda') : 'white'
                          }}
                        >
                          {has && (
                            <span style={{ 
                              color: isWildcard && perm !== '*' ? '#388e3c' : '#28a745',
                              fontWeight: 600
                            }}>
                              ✓
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                  
                  {/* Expanded Users Row */}
                  {isExpanded && (
                    <tr>
                      <td 
                        colSpan={activePermissions.length + 1}
                        style={{ 
                          padding: '1rem', 
                          backgroundColor: '#f8f9fa',
                          borderBottom: '1px solid #dee2e6'
                        }}
                      >
                        <div style={{ marginBottom: '0.5rem', fontWeight: 500 }}>
                          Users with this role:
                        </div>
                        {loadingUsers ? (
                          <span style={{ color: '#666' }}>Loading...</span>
                        ) : roleUsers.length === 0 ? (
                          <span style={{ color: '#666', fontStyle: 'italic' }}>
                            No users assigned to this role
                          </span>
                        ) : (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                            {roleUsers.map(user => (
                              <span
                                key={user.id}
                                style={{
                                  padding: '0.25rem 0.5rem',
                                  backgroundColor: user.is_primary ? '#007bff' : '#6c757d',
                                  color: 'white',
                                  borderRadius: '4px',
                                  fontSize: '0.8rem'
                                }}
                                title={user.is_primary ? 'Primary Role' : 'Secondary Role'}
                              >
                                {user.display_name || user.email}
                                {!user.is_primary && ' (2nd)'}
                              </span>
                            ))}
                          </div>
                        )}
                        
                        {/* Role Details */}
                        <div style={{ 
                          marginTop: '1rem', 
                          padding: '0.75rem',
                          backgroundColor: 'white',
                          borderRadius: '4px',
                          fontSize: '0.85rem'
                        }}>
                          <div style={{ marginBottom: '0.5rem' }}>
                            <strong>Scope:</strong> {role.scope || 'N/A'}
                          </div>
                          <div style={{ marginBottom: '0.5rem' }}>
                            <strong>Default Sub-Departments:</strong>{' '}
                            {role.default_sub_departments === '*' ? 'All' : 
                             role.default_sub_departments || 'None (manual assignment)'}
                          </div>
                          <div>
                            <strong>Permissions:</strong>{' '}
                            <code style={{ 
                              fontSize: '0.8rem',
                              backgroundColor: '#f8f9fa',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px'
                            }}>
                              {(role.permissions || []).join(', ') || 'None'}
                            </code>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Summary */}
      <div style={{ 
        marginTop: '1rem', 
        color: '#666', 
        fontSize: '0.9rem',
        display: 'flex',
        gap: '2rem'
      }}>
        <span>
          <strong>{filteredRoles.length}</strong> roles
        </span>
        <span>
          <strong>{activePermissions.length}</strong> permissions
        </span>
      </div>
    </div>
  );
};

export default PermissionMatrixTab;
